import { inject, Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import {
  API_ROUTE_UMMELDUNG,
  API_ROUTE_UMMELDUNG_DOCUMENT,
  API_ROUTE_ZAEHLPUNKTE,
  getApiUrl,
  PDF_FILE_EXTENSION,
  UMMELDUNG_DATE_FORMAT,
  UMMELDUNG_FILE_NAME_PREFIX,
  UMMELDUNG_FILE_ZNR,
} from '../../app.constants';
import { forkJoin, map, Observable, of, switchMap, throwError } from 'rxjs';
import { FileModel } from '@shared-ikp/components/inputs/file-input/file-input.model';
import {
  MeterPointChangeDocumentResponse,
  MeterPointChangeState,
} from '../../pages/meter-point-change-request/meterpoint-change-request.types';

import { catchError } from 'rxjs/operators';
import dayjs from 'dayjs';
import { MeterPointChangeTransformer } from './meter-point-change.transformer';
import { MeterPointService } from './meter-point.service';
import { IkpPopupService } from '@shared-ikp/service/popup/popup.service';
import { IPopupModel } from '@mwe/models';

@Injectable({
  providedIn: 'root',
})
export class MeterPointChangeService extends ApiService {
  private popupService = inject(IkpPopupService);
  private meterPointService = inject(MeterPointService);

  public uploadSignedMeterpointChange(contractNumber: string, meterpointNumber: string, fileToUpload: FileModel): Observable<void> {
    const url = `${getApiUrl(this.apiUrl, API_ROUTE_ZAEHLPUNKTE)}/${contractNumber}/${meterpointNumber}/${API_ROUTE_UMMELDUNG_DOCUMENT}`;
    const formData = new FormData();
    formData.append('file', fileToUpload.originalFile, fileToUpload.name);
    return this.httpClient.post(url, formData).pipe(map(() => null));
  }

  public deleteMeterpointChange(contractNumber: string, meterPointNumber: string): Observable<void> {
    const url = `${getApiUrl(this.apiUrl, API_ROUTE_ZAEHLPUNKTE)}/${contractNumber}/${meterPointNumber}/${API_ROUTE_UMMELDUNG}`;
    return this.httpClient.delete(url).pipe(map(() => null));
  }

  public getMeterPointChangeRequest(contractNumber: string, meterPointNumber: string): Observable<Blob> {
    const apiBasePath = getApiUrl(this.apiUrl, `${API_ROUTE_ZAEHLPUNKTE}/${contractNumber}/${meterPointNumber}`);
    return this.httpClient
      .get(`${apiBasePath}/${API_ROUTE_UMMELDUNG_DOCUMENT}`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(map(response => response.body));
  }

  public createDocument(meterPointChangeState: MeterPointChangeState): Observable<MeterPointChangeDocumentResponse> {
    if (meterPointChangeState.productAndDate.meterPoints.length === 0) {
      return of(null);
    }
    const meterPoint = meterPointChangeState.meterPoint;
    const meterPointNumber = meterPoint.zaehlpunktnummer;
    const contractNumber = meterPoint.vertragsnummer;
    const meterNumber = meterPointChangeState.productAndDate.meterPoints.at(0).meterPointNumber;
    const apiBasePath = getApiUrl(this.apiUrl, `${API_ROUTE_ZAEHLPUNKTE}/${contractNumber}/${meterPointNumber}`);
    const meterPointChangeRequestPayload = MeterPointChangeTransformer.mapToIMeterPointChangeRequest(meterPointChangeState);

    return this.httpClient.put(`${apiBasePath}/${API_ROUTE_UMMELDUNG}`, meterPointChangeRequestPayload).pipe(
      switchMap(() =>
        forkJoin({
          blob: this.getMeterPointChangeRequest(contractNumber, meterPointNumber),
          updatedMeterPoint: this.meterPointService.loadMeterPoint(contractNumber, meterPoint.id),
        }),
      ),
      map(({ blob, updatedMeterPoint }) => ({
        blob: blob,
        filename: this.composeDocumentName(meterNumber),
        updatedMeterPoint: updatedMeterPoint || null,
      })),
      catchError(error => throwError(() => error)),
    );
  }

  public handleDownloadErrorPopup(): void {
    this.popupService.handleGenericPopup(this.DOWNLOAD_ERROR_POPUP, true);
  }

  public handleGenericErrorPopup(id: string): void {
    this.popupService.handleErrorPopup(id, 'global.errors.meterpointChangeRequest.defaultErrorTitle');
  }

  public composeDocumentName(meterNumber: string): string {
    const date = dayjs().format(UMMELDUNG_DATE_FORMAT);
    return [UMMELDUNG_FILE_NAME_PREFIX, date, UMMELDUNG_FILE_ZNR, meterNumber, PDF_FILE_EXTENSION].join('');
  }

  private readonly DOWNLOAD_ERROR_POPUP: IPopupModel = {
    id: 'download-error',
    titleKey: 'global.errors.meterpointChangeRequest.download.title',
    messageKey: 'global.errors.meterpointChangeRequest.download.message',
    showSubmitButton: true,
    showCancelButton: true,
    submitButtonKey: 'global.errors.meterpointChangeRequest.download.primaryLink',
    cancelButtonKey: 'global.errors.meterpointChangeRequest.download.secondaryLink',
    iconColorClass: 'red',
    iconTypeClass: 'fa-bell',
  };
}
